// `(fuzzy-match ds :col "pattern" :threshold 0.4)` — keep only the rows
// whose `:col` fuzzy-matches `pattern`, ordered best-match-first.
//
// This is a DATASET-level verb, deliberately distinct from the scalar
// `(fuzzy …)` predicate. Ranking is inherently a whole-dataset
// operation: the scalar `fuzzy` runs once per row and only ever sees a
// single value, so it can return a boolean but can never order the
// matches — the closeness score it computes is discarded. `fuzzy-match`
// computes that score here (over the whole column), orders rows ascending
// by it (0 = exact, 1 = no match), then drops the synthetic score column.
//
// The payoff is that "find the single closest record" is correct by
// construction: `(-> ds (fuzzy-match :name "ADNOC Offshore") (limit 1))`
// always yields the best match, whereas `(filter #(fuzzy …)) (limit 1)`
// returns an arbitrary in-threshold row (every ADNOC company matches at
// the default threshold, and `limit` just takes whichever came first).

import * as aq from 'arquero';
import { z } from 'zod';

import { prefixStringify } from '../../builtins/helpers.ts';
import { buildFuzzySearcher } from '../../builtins/string-match.ts';
import type { EvalResult } from '../../env.ts';
import { ShapeError } from '../../errors.ts';
import { type AstInput, astShape, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { withTable } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { passThroughSchema } from '../../schema/infer.ts';
import { dropSynthetic, requireColumns, resolveDsArg, singleColRefAt } from './helpers.ts';

// Synthetic column holding the per-row Bitap distance used purely to
// order the result; dropped before the dataset escapes the verb so it
// never leaks downstream.
const SCORE_COL = '__fuzzy_match_score__';

/**
 * `(fuzzy-match ds :col "pattern" :threshold 0.4)` — filter `ds` to rows
 * whose `:col` fuzzy-matches `pattern`, ordered best match first.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError` (via `resolveDsArg`).
 *   - Column arg isn't a keyword col-ref (`:name`) → `ShapeError`.
 *   - Column doesn't exist on `ds.schema` → `UnknownColumnRefError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before matching.
 * The row order is best-first (ascending distance); compose with
 * `(limit 1)` for the single closest row.
 */
export const fuzzyMatch = z
  .object({
    name: z.literal('fuzzy-match'),
    params: z.tuple([astArg('<dataset>'), astArg('<column>'), astArg('<pattern>')]),
    threshold: z.number().min(0).max(1).optional(),
  })
  .describe(
    'Keep rows whose :column fuzzy-matches pattern, best match first. (fuzzy-match ds :name "ADNOC" :threshold 0.4) — Bitap; 0 = exact, 1 = anything; default 0.4. Case-insensitive. Compose with (limit 1) for the single closest row.',
  )
  .register(opMeta, {
    // Special evaluation: the column arg is a raw keyword AST (not a
    // useful Value under eager dispatch), so we walk the AST to extract
    // the column name and evaluate the dataset / pattern args by hand.
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(-> group-companies (fuzzy-match :name "ADNOC Offshore") (limit 1))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [dsAst, colAst, patAst] = data.params as readonly [AstInput, AstInput, AstInput];
      const threshold = (data as { readonly threshold?: number }).threshold ?? 0.4;

      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(fuzzy-match ds :col pat)', ctx);

      const colShape = astShape(colAst);
      if (colShape.kind !== 'keyword') {
        throw new ShapeError(
          'fuzzy-match column must be a keyword column ref like `:name`',
          nodePos(ctx.spans, paramPath(ctx.path, 1)),
        );
      }
      const column = colShape.name;
      requireColumns([column], concrete.schema, () => nodePos(ctx.spans, paramPath(ctx.path, 1)));

      const { value: patValue } = await ctx.evalNode(patAst, ctx.env, paramPath(ctx.path, 2));
      const searcher = buildFuzzySearcher(String(patValue), threshold);

      // One Bitap pass per row: lift the distance into a synthetic
      // column (`null` for non-matches), drop the non-matches, order
      // ascending (best first), then drop the synthetic so it never
      // escapes the verb.
      const scored = withTable(concrete, (t) =>
        t.derive({
          [SCORE_COL]: aq.escape((d: Record<string, unknown>) => {
            const result = searcher.searchIn(prefixStringify(d[column]));
            return result.isMatch ? result.score : null;
          }),
        }),
      );
      const matched = withTable(scored, (t) =>
        t.filter(aq.escape((d: Record<string, unknown>) => d[SCORE_COL] !== null)),
      );
      const sorted = withTable(matched, (t) => t.orderby(SCORE_COL));
      return { value: dropSynthetic(sorted, [SCORE_COL]), env: ctx.env };
    },
    dataset: {
      // Row filter + reorder only: columns and types are unchanged (the
      // synthetic score column never escapes).
      propagateSchema: passThroughSchema,
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => singleColRefAt(data, basePath, 1),
    },
  });
